import React, { useState } from 'react'
import { CheckCircle, Calendar, MapPin, DollarSign, Package, Users, Settings, Image as ImageIcon, AlertCircle } from 'lucide-react'
import { EventFormData } from '../EventCreationWizard'
import LoadingSpinner from '../../ui/LoadingSpinner'
import EventPreviewCard from './EventPreviewCard'

interface ReviewPublishStepProps {
  formData: EventFormData
  updateFormData: (updates: Partial<EventFormData>) => void
  onNext: () => void
  onPrev: () => void
  isFirstStep: boolean
  isLastStep: boolean
  onPublish?: () => Promise<void>
}

const ReviewPublishStep: React.FC<ReviewPublishStepProps> = ({
  formData,
  onPrev,
  onPublish
}) => {
  const [publishing, setPublishing] = useState(false)

  const handlePublish = async () => {
    if (!onPublish) return

    setPublishing(true)
    try {
      await onPublish()
    } catch (error) {
      console.error('Publish error:', error)
    } finally {
      setPublishing(false)
    }
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Not set'
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const formatDateTime = (dateString: string) => {
    if (!dateString) return 'Not set'
    return new Date(dateString).toLocaleString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    })
  }

  const totalMenuItems = formData.menuItems.length
  const totalInventory = formData.menuItems.reduce((sum, item) => {
    return sum + (item.inventory || 0)
  }, 0)
  const hasUnlimitedItems = formData.menuItems.some(item => item.inventory === null)

  // Phase 2: Calculate total lobster tail capacity
  const totalLobsterTailCapacity = formData.menuItems.reduce((sum, item) => {
    if (item.inventory === null) return Infinity
    return sum + (item.lobster_tail_qty || 0) * item.inventory
  }, 0)

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground mb-2">Review & Publish</h2>
        <p className="text-muted-foreground">
          Review your event details before publishing
        </p>
      </div>

      {/* Live Preview Section */}
      <div>
        <div className="border-l-4 border-primary pl-4">
          <h3 className="text-3xl font-bold text-foreground">Live Preview</h3>
        </div>
        <p className="text-lg text-muted-foreground mt-1 mb-4">This is how your event will appear to customers.</p>

        <EventPreviewCard formData={formData} />
      </div>

      {/* Event Details Summary */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center space-x-2">
          <ImageIcon className="text-primary" size={20} />
          <span>Event Details</span>
        </h3>
        <div className="space-y-3">
          <div>
            <span className="text-sm font-medium text-muted-foreground">Title:</span>
            <p className="text-foreground">{formData.title}</p>
          </div>
          {formData.description && (
            <div>
              <span className="text-sm font-medium text-muted-foreground">Description:</span>
              <p className="text-foreground text-sm">{formData.description}</p>
            </div>
          )}
          <div>
            <span className="text-sm font-medium text-muted-foreground">Event Image:</span>
            <p className="text-foreground text-sm">{formData.image_url ? '✓ Uploaded' : '⚠ Not uploaded'}</p>
          </div>
        </div>
      </div>

      {/* Menu & Products Summary */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center space-x-2">
          <Package className="text-primary" size={20} />
          <span>Menu & Products</span>
        </h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-muted-foreground">Total Items:</span>
            <span className="text-foreground font-semibold">{totalMenuItems}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-muted-foreground">Max Lobster Tails:</span>
            <span className="text-foreground font-semibold">
              {formData.max_lobster_tails ? `🦞 ${formData.max_lobster_tails} tails` : 'Unlimited'}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-muted-foreground">Tails per Order Limit:</span>
            <span className="text-foreground font-semibold">
              {formData.max_tails_per_order || 'Unlimited'}
            </span>
          </div>
        </div>

        {formData.menuItems.length > 0 && (
          <div className="mt-4 pt-4 border-t border-border">
            <p className="text-xs font-medium text-muted-foreground mb-2">Menu Items:</p>
            <div className="space-y-2">
              {formData.menuItems.map((item, index) => (
                <div key={item.id} className="flex items-start justify-between text-sm py-2">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <span className="text-muted-foreground">{index + 1}.</span>
                      <span className="text-foreground font-medium">{item.name}</span>
                    </div>
                    {item.lobster_tail_qty > 0 && (
                      <div className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800 mt-1 ml-6">
                        <span className="mr-1">🦞</span>
                        {item.lobster_tail_qty} lobster tail{item.lobster_tail_qty > 1 ? 's' : ''}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center space-x-3 ml-4">
                    <span className="text-primary font-semibold">${item.price.toFixed(2)}</span>
                    <span className="text-xs text-muted-foreground whitespace-nowrap">
                      {item.inventory === null ? '∞ avail.' : `${item.inventory} avail.`}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Location & Schedule Summary */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center space-x-2">
          <MapPin className="text-primary" size={20} />
          <span>Location & Schedule</span>
        </h3>
        <div className="space-y-3">
          <div>
            <span className="text-sm font-medium text-muted-foreground">Location:</span>
            <p className="text-foreground">{formData.location_name}</p>
            <p className="text-sm text-muted-foreground">{formData.address}, {formData.zip_code}</p>
          </div>
          <div className="pt-3 border-t border-border">
            <p className="text-xs font-medium text-muted-foreground mb-2">Timeline:</p>
            <div className="space-y-1 text-sm">
              {formData.notification_date && (
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 rounded-full bg-primary" />
                  <span className="text-muted-foreground">Notification:</span>
                  <span className="text-foreground">{formatDate(formData.notification_date)}</span>
                </div>
              )}
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 rounded-full bg-primary" />
                <span className="text-muted-foreground">Registration Opens:</span>
                <span className="text-foreground">{formatDateTime(formData.registration_opens)}</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 rounded-full bg-primary" />
                <span className="text-muted-foreground">Registration Closes:</span>
                <span className="text-foreground">{formatDateTime(formData.registration_closes)}</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 rounded-full bg-green-500" />
                <span className="text-muted-foreground">Event Date:</span>
                <span className="text-foreground">{formatDate(formData.event_date)}</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 rounded-full bg-amber-500" />
                <span className="text-muted-foreground">Pickup Window:</span>
                <span className="text-foreground">
                  {formatDateTime(formData.pickup_start_time)} - {formatDateTime(formData.pickup_end_time)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Rules & Settings Summary */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center space-x-2">
          <Settings className="text-primary" size={20} />
          <span>Rules & Settings</span>
        </h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <span className="text-sm font-medium text-muted-foreground">Status:</span>
            <p className="text-foreground">
              {formData.status === 'draft' ? (
                <span className="inline-flex items-center px-2 py-1 bg-muted text-foreground text-sm rounded">
                  Draft
                </span>
              ) : (
                <span className="inline-flex items-center px-2 py-1 bg-green-500/10 text-green-600 text-sm rounded">
                  Scheduled
                </span>
              )}
            </p>
          </div>
          <div>
            <span className="text-sm font-medium text-muted-foreground">Max Orders:</span>
            <p className="text-foreground">{formData.max_orders || 'Unlimited'}</p>
          </div>
          <div>
            <span className="text-sm font-medium text-muted-foreground">Items per Order:</span>
            <p className="text-foreground">{formData.pre_order_limit || 'Unlimited'}</p>
          </div>
          <div>
            <span className="text-sm font-medium text-muted-foreground">Max Lobster Tails:</span>
            <p className="text-foreground">{formData.max_lobster_tails || 'Unlimited'}</p>
          </div>
          <div>
            <span className="text-sm font-medium text-muted-foreground">Tails per Order:</span>
            <p className="text-foreground">{formData.max_tails_per_order || 'Unlimited'}</p>
          </div>
        </div>
      </div>

      {/* Status Banner */}
      {formData.status === 'scheduled' ? (
        <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-5">
          <div className="flex items-center space-x-4">
            <CheckCircle className="text-green-700 flex-shrink-0" size={28} />
            <div>
              <h4 className="text-base font-bold text-green-800 mb-1">Ready to Publish</h4>
              <p className="text-sm text-green-700">
                This event will be published and visible to SECRET members immediately after you click "Publish Event".
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-muted border border-border rounded-lg p-5">
          <div className="flex items-center space-x-4">
            <AlertCircle className="text-muted-foreground flex-shrink-0" size={28} />
            <div>
              <h4 className="text-base font-bold text-foreground mb-1">Save as Draft</h4>
              <p className="text-sm text-muted-foreground">
                This event will be saved as a draft and will not be visible to anyone. You can publish it later from the admin dashboard.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ReviewPublishStep
